import { EGlobalSearchItemType } from "@frontend-shared/types";
import type { ParseKeys } from "i18next";
import type { LucideIcon } from "lucide-react";
import {
  Bell,
  Bot,
  Brain,
  Building2,
  CalendarDays,
  CircleHelp,
  Cpu,
  FileOutput,
  FileText,
  Folder,
  FolderKanban,
  KeyRound,
  LayoutGrid,
  Library,
  Mail,
  MessageSquare,
  MessagesSquare,
  NotebookPen,
  Puzzle,
  ScanText,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Store,
  UserRound,
  Video,
  Workflow,
  Wrench,
} from "lucide-react";
import type { CSSProperties } from "react";

// NOTE: Every item type has a group heading; a type added on the server
// before this map learns it falls back to its raw value (see GlobalSearchGroup).
export const GLOBAL_SEARCH_GROUP_TITLE_KEYS: Readonly<
  Record<EGlobalSearchItemType, ParseKeys<"globalSearch">>
> = {
  [EGlobalSearchItemType.ASSISTANT]: "groups.assistant",
  [EGlobalSearchItemType.PROMPT]: "groups.prompt",
  [EGlobalSearchItemType.CUSTOM_TOOL]: "groups.custom_tool",
  [EGlobalSearchItemType.CUSTOM_INTEGRATION]: "groups.custom_integration",
  [EGlobalSearchItemType.INTEGRATION_CONNECTION]:
    "groups.integration_connection",
  [EGlobalSearchItemType.SKILL]: "groups.skill",
  [EGlobalSearchItemType.CATALOG_SKILL]: "groups.catalog_skill",
  [EGlobalSearchItemType.AUTOMATION]: "groups.automation",
  [EGlobalSearchItemType.MARKETPLACE_AUTOMATION]:
    "groups.marketplace_automation",
  [EGlobalSearchItemType.MODEL]: "groups.model",
  [EGlobalSearchItemType.KNOWLEDGE_FILE]: "groups.knowledge_file",
  [EGlobalSearchItemType.MEETING]: "groups.meeting",
  [EGlobalSearchItemType.CALENDAR_EVENT]: "groups.calendar_event",
  [EGlobalSearchItemType.GENERATED_FILE]: "groups.generated_file",
  [EGlobalSearchItemType.DOC_RECOGNITION_DOCUMENT]:
    "groups.doc_recognition_document",
  [EGlobalSearchItemType.CHAT_FOLDER]: "groups.chat_folder",
  [EGlobalSearchItemType.MEMBER]: "groups.member",
  [EGlobalSearchItemType.INVITE]: "groups.invite",
  [EGlobalSearchItemType.ROLE]: "groups.role",
  [EGlobalSearchItemType.API_KEY]: "groups.api_key",
  [EGlobalSearchItemType.MEMORY_FACT]: "groups.memory_fact",
  [EGlobalSearchItemType.CUSTOM_INSTRUCTIONS]: "groups.custom_instructions",
  [EGlobalSearchItemType.PROBE]: "groups.probe",
  [EGlobalSearchItemType.CHAT]: "groups.chat",
  [EGlobalSearchItemType.CHAT_MESSAGE]: "groups.chat_message",
  [EGlobalSearchItemType.PAGE]: "groups.page",
  [EGlobalSearchItemType.SETTINGS_TAB]: "groups.settings_tab",
  [EGlobalSearchItemType.SETTINGS_OPTION]: "groups.settings_option",
  [EGlobalSearchItemType.INTEGRATION_PROVIDER]: "groups.integration_provider",
  [EGlobalSearchItemType.PROJECT]: "groups.project",
  [EGlobalSearchItemType.ORGANIZATION]: "groups.organization",
  [EGlobalSearchItemType.FAQ_ITEM]: "groups.faq_item",
  [EGlobalSearchItemType.NOTIFICATION]: "groups.notification",
  [EGlobalSearchItemType.ACTION]: "groups.action",
};

export const GLOBAL_SEARCH_ITEM_ICONS: Readonly<
  Record<EGlobalSearchItemType, LucideIcon>
> = {
  [EGlobalSearchItemType.ASSISTANT]: Bot,
  [EGlobalSearchItemType.PROMPT]: NotebookPen,
  [EGlobalSearchItemType.CUSTOM_TOOL]: Wrench,
  [EGlobalSearchItemType.CUSTOM_INTEGRATION]: Puzzle,
  [EGlobalSearchItemType.INTEGRATION_CONNECTION]: Puzzle,
  [EGlobalSearchItemType.SKILL]: Sparkles,
  [EGlobalSearchItemType.CATALOG_SKILL]: Library,
  [EGlobalSearchItemType.AUTOMATION]: Workflow,
  [EGlobalSearchItemType.MARKETPLACE_AUTOMATION]: Store,
  [EGlobalSearchItemType.MODEL]: Cpu,
  [EGlobalSearchItemType.KNOWLEDGE_FILE]: FileText,
  [EGlobalSearchItemType.MEETING]: Video,
  [EGlobalSearchItemType.CALENDAR_EVENT]: CalendarDays,
  [EGlobalSearchItemType.GENERATED_FILE]: FileOutput,
  [EGlobalSearchItemType.DOC_RECOGNITION_DOCUMENT]: ScanText,
  [EGlobalSearchItemType.CHAT_FOLDER]: Folder,
  [EGlobalSearchItemType.MEMBER]: UserRound,
  [EGlobalSearchItemType.INVITE]: Mail,
  [EGlobalSearchItemType.ROLE]: ShieldCheck,
  [EGlobalSearchItemType.API_KEY]: KeyRound,
  [EGlobalSearchItemType.MEMORY_FACT]: Brain,
  [EGlobalSearchItemType.CUSTOM_INSTRUCTIONS]: NotebookPen,
  [EGlobalSearchItemType.PROBE]: ShieldAlert,
  [EGlobalSearchItemType.CHAT]: MessageSquare,
  [EGlobalSearchItemType.CHAT_MESSAGE]: MessagesSquare,
  [EGlobalSearchItemType.PAGE]: LayoutGrid,
  [EGlobalSearchItemType.SETTINGS_TAB]: Settings,
  [EGlobalSearchItemType.SETTINGS_OPTION]: SlidersHorizontal,
  [EGlobalSearchItemType.INTEGRATION_PROVIDER]: Puzzle,
  [EGlobalSearchItemType.PROJECT]: FolderKanban,
  [EGlobalSearchItemType.ORGANIZATION]: Building2,
  [EGlobalSearchItemType.FAQ_ITEM]: CircleHelp,
  [EGlobalSearchItemType.NOTIFICATION]: Bell,
  [EGlobalSearchItemType.ACTION]: Search,
};

// NOTE: NSAI-6812 — a result whose destination has a sidebar entry wears that
// entry's glyph (NavigationIcon) instead of the type icon above. The sidebar
// draws it lighter; here it takes the stroke of the type icons beside it.
export const GLOBAL_SEARCH_NAVIGATION_ICON_STYLE: Readonly<CSSProperties> = {
  strokeWidth: 2,
};

// NOTE: The values a result's `params.badge` may carry (MEETING sends
// "uploaded"); each maps to a globalSearch:badges.* key.
export const GLOBAL_SEARCH_BADGE_KEYS: Readonly<
  Record<string, ParseKeys<"globalSearch">>
> = {
  uploaded: "badges.uploaded",
  shared: "badges.shared",
  public: "badges.public",
  personal: "badges.personal",
  semantic: "badges.semantic",
};
